/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * "Find in Store" button (PureMoto / PureIRL). Keeps the button's
 * `data-pureirl-sku` in sync with the selected variant's barcode and resets
 * the PureIRL widget so it re-queries availability for the new variant.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} pureirlButton
 *
 * @extends {Component<Refs>}
 */
class FindInStoreComponent extends Component {
  requiredRefs = ['pureirlButton'];

  /** @type {number | null} */
  #pureirlPollId = null;

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
    this.#waitForPureIRLReady();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearPureirlPoll();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  /** @param {CustomEvent} event */
  updateSku = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (!event.detail.resource) return;

    this.refs.pureirlButton.dataset.pureirlSku = event.detail.resource.barcode || '';

    if (window.PureIRL && window.PureIRL.STATUS === 2) {
      window.PureIRL._reset();
      this.#waitForPureIRLReady();
    }
  };

  #clearPureirlPoll() {
    if (this.#pureirlPollId == null) return;
    cancelAnimationFrame(this.#pureirlPollId);
    this.#pureirlPollId = null;
  }

  #waitForPureIRLReady() {
    this.#clearPureirlPoll();
    this.#setLoading(true);

    const startedAt = Date.now();
    const minDuration = 200;
    const deadline = startedAt + 15000;

    const poll = () => {
      const elapsed = Date.now() - startedAt;
      const pureirlReady = window.PureIRL?.STATUS === 2;

      if ((pureirlReady && elapsed >= minDuration) || Date.now() > deadline) {
        this.#setLoading(false);
        this.#pureirlPollId = null;
        return;
      }

      this.#pureirlPollId = requestAnimationFrame(poll);
    };

    this.#pureirlPollId = requestAnimationFrame(poll);
  }

  /** @param {boolean} isLoading */
  #setLoading(isLoading) {
    const button = this.refs.pureirlButton;
    if (!button) return;

    if (isLoading) {
      this.dataset.state = 'loading';
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      return;
    }

    this.dataset.state = 'ready';
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }
}

if (!customElements.get('find-in-store-component')) {
  customElements.define('find-in-store-component', FindInStoreComponent);
}
